import { useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import '../styles/calendar.css';

const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

/** „2026-11“ plus n Monate */
export const addMonths = (ym: string, n: number): string => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
};
/** Alle Tage zwischen zwei ISO-Daten (beide eingeschlossen, Reihenfolge egal) */
export function daysBetween(a: string, b: string): string[] {
  const [from, to] = a <= b ? [a, b] : [b, a];
  const out: string[] = [];
  for (let d = new Date(from + 'T00:00:00Z'); d.toISOString().slice(0, 10) <= to; d = new Date(d.getTime() + 86400_000)) out.push(d.toISOString().slice(0, 10));
  return out;
}
export const isSunday = (iso: string) => new Date(iso + 'T00:00:00Z').getUTCDay() === 0;

/** Tage eines Monats als Wochenzeilen (Montag zuerst), Lücken als null */
function weeksOf(ym: string): (string | null)[][] {
  const first = new Date(ym + '-01T00:00:00Z');
  const lead = (first.getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  const cells: (string | null)[] = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => `${ym}-${String(i + 1).padStart(2, '0')}`)];
  while (cells.length % 7) cells.push(null);
  return Array.from({ length: cells.length / 7 }, (_, w) => cells.slice(w * 7, w * 7 + 7));
}

export interface MonthCalendarProps {
  /** erster sichtbarer Monat „YYYY-MM“ */
  start: string;
  /** Anzahl Monate nebeneinander (auf schmalen Bildschirmen untereinander) */
  months?: number;
  /** Blättern begrenzen */
  min?: string;
  max?: string;
  /** Tag anklickbar? */
  enabled: (iso: string) => boolean;
  /** zusätzliche Klassen je Tag, z. B. „sel“, „offer“, „yes“ */
  dayClass?: (iso: string) => string;
  /** kleiner Zusatz unter der Zahl */
  dayExtra?: (iso: string) => ReactNode;
  dayTitle?: (iso: string) => string | undefined;
  onDayClick: (iso: string) => void;
  /** Zeitraum durch Ziehen; ohne diese Funktion nur Einzelklicks */
  onRange?: (from: string, to: string, anchor: string) => void;
  today?: string;
}

/** Monatskalender zum Anklicken einzelner Tage und Aufziehen von Zeiträumen (Maus und Touch) */
export function MonthCalendar({ start, months = 2, min, max, enabled, dayClass, dayExtra, dayTitle, onDayClick, onRange, today }: MonthCalendarProps) {
  const [first, setFirst] = useState(start);
  const [drag, setDragState] = useState<{ anchor: string; cur: string } | null>(null);
  // Ref spiegelt den Zustand, damit ein schneller Klick (Drücken und Loslassen ohne Rendern dazwischen) nicht verloren geht
  const dragRef = useRef<{ anchor: string; cur: string } | null>(null);
  const setDrag = (v: { anchor: string; cur: string } | null) => {
    dragRef.current = v;
    setDragState(v);
  };
  const shown = Array.from({ length: months }, (_, i) => addMonths(first, i));
  const canPrev = !min || first > min;
  const canNext = !max || addMonths(first, months - 1) < max;
  const inDrag = (iso: string) => !!drag && drag.anchor !== drag.cur && iso >= (drag.anchor < drag.cur ? drag.anchor : drag.cur) && iso <= (drag.anchor < drag.cur ? drag.cur : drag.anchor);

  const dayAt = (x: number, y: number): string | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-day]');
    return el && el.dataset.enabled === '1' ? el.dataset.day! : null;
  };
  const down = (e: React.PointerEvent, iso: string) => {
    if (!enabled(iso)) return;
    if (onRange) {
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      setDrag({ anchor: iso, cur: iso });
    }
  };
  const move = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const iso = dayAt(e.clientX, e.clientY);
    if (iso && iso !== d.cur) setDrag({ ...d, cur: iso });
  };
  const up = () => {
    const d = dragRef.current;
    if (!d) return;
    setDrag(null);
    if (d.anchor !== d.cur) onRange?.(d.anchor, d.cur, d.anchor);
    else onDayClick(d.anchor);
  };

  return (
    <div className={`mcal ${onRange ? 'drag' : ''}`} onPointerMove={move} onPointerUp={up} onPointerCancel={() => setDrag(null)} onPointerLeave={(e) => e.buttons === 0 && setDrag(null)}>
      {(canPrev || canNext) && (
        <div className="mcal-nav">
          <button type="button" className="icon-btn" aria-label="Vorheriger Monat" disabled={!canPrev} onClick={() => setFirst(addMonths(first, -1))}>
            <Icon name="chev" />
          </button>
          <button type="button" className="icon-btn" aria-label="Nächster Monat" disabled={!canNext} onClick={() => setFirst(addMonths(first, 1))}>
            <Icon name="chev" />
          </button>
        </div>
      )}
      <div className="mcal-months">
        {shown.map((ym) => (
          <div className="mcal-month" key={ym}>
            <div className="mcal-title">
              {MONTHS[Number(ym.slice(5, 7)) - 1]} {ym.slice(0, 4)}
            </div>
            <div className="mcal-grid" role="grid">
              {WEEKDAYS.map((w) => (
                <div key={w} className="mcal-wd" role="columnheader">
                  {w}
                </div>
              ))}
              {weeksOf(ym)
                .flat()
                .map((iso, i) =>
                  iso ? (
                    <button
                      key={iso}
                      type="button"
                      role="gridcell"
                      data-day={iso}
                      data-enabled={enabled(iso) ? '1' : '0'}
                      disabled={!enabled(iso)}
                      title={dayTitle?.(iso)}
                      className={`mcal-day ${dayClass?.(iso) ?? ''} ${inDrag(iso) ? 'range' : ''} ${iso === today ? 'today' : ''} ${isSunday(iso) ? 'sun' : ''}`}
                      onPointerDown={(e) => down(e, iso)}
                      onClick={() => !onRange && enabled(iso) && onDayClick(iso)}
                      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onRange && (e.preventDefault(), onDayClick(iso))}
                    >
                      <span className="n">{Number(iso.slice(8, 10))}</span>
                      {dayExtra?.(iso)}
                    </button>
                  ) : (
                    <div key={`e${i}`} className="mcal-empty" />
                  ),
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
